import { dayMonthEs, shortDayMonthEs } from "./dateFormat";

// «Publicar horarios» (Eduardo 2026-10-01): textos del aviso fijo del
// calendario, del diálogo de confirmación y del mensaje al terminar. Viven
// aquí (lógica pura) para probarlos sin montar la página.
//
// Regla de palabras: siempre «Publicar horarios» (nunca «Publicar» a secas,
// que ya es publicar el torneo o un resultado) y «Sin publicar» (nunca
// «Borrador», que ya es un torneo o un matchup de escalera).

/** «1 horario» / «12 horarios». */
export function horariosText(count: number): string {
  return count === 1 ? "1 horario" : `${count} horarios`;
}

/** «1 jugador» / «22 jugadores». */
export function jugadoresText(count: number): string {
  return count === 1 ? "1 jugador" : `${count} jugadores`;
}

/** «1 día» / «3 días». */
function diasText(count: number): string {
  return count === 1 ? "1 día" : `${count} días`;
}

/** Encabezado del aviso fijo del calendario para el día en pantalla. */
export function unpublishedHeadline(count: number): string {
  return count === 1
    ? "Tienes 1 horario sin publicar: los jugadores aún no lo ven."
    : `Tienes ${count} horarios sin publicar: los jugadores aún no los ven.`;
}

/** Aviso corto arriba de la página (la lista va antes que el calendario). */
export function unpublishedTopSummary(input: {
  total: number;
  days: number;
  withoutDate: number;
  divisionName?: string | null;
}): string {
  const { total, days, withoutDate, divisionName } = input;
  const who = divisionName ? `${divisionName} tiene` : "Tienes";
  const parts: string[] = [];
  const byDay = total - withoutDate;
  if (byDay > 0) {
    parts.push(`${who} ${horariosText(byDay)} sin publicar en ${diasText(days)}.`);
  }
  if (withoutDate > 0) {
    parts.push(
      byDay > 0
        ? `Y ${withoutDate === 1 ? "1 tiene" : `${withoutDate} tienen`} hora o cancha sin fecha: ponles fecha para poder publicarlos.`
        : `${who} ${horariosText(withoutDate)} con hora o cancha pero sin fecha: ponles fecha para poder publicarlos.`,
    );
  }
  parts.push(total === 1 ? "Los jugadores aún no lo ven." : "Los jugadores aún no los ven.");
  return parts.join(" ");
}

/** «Sub-12 · sábado 4 de octubre» (o solo el día sin categoría). */
export function publishScopeLabel(divisionName: string | null | undefined, date: string): string {
  const day = dayMonthEs(date) || date;
  return divisionName ? `${divisionName} · ${day}` : day;
}

export interface PublishConfirmInput {
  /** Cuántos publica el servidor (vista previa `dry_run`). */
  count: number;
  date: string;
  divisionName?: string | null;
  /** Jugadores que reciben el aviso (vista previa). */
  players: number;
  /** false = avisos del torneo apagados (cerrado o reabierto sin avisar). */
  noticesOpen: boolean;
  /** El torneo está cerrado (solo el superadmin llega aquí). */
  closed?: boolean;
  /** Cuántos de los que se publican no se ven por los filtros de la pantalla. */
  hiddenByFilters?: number;
  /** Cuántos se publican en otras sedes (no las de la pantalla). */
  otherVenues?: Array<{ name: string; count: number }>;
}

/** Párrafos (separados por \n) del diálogo de «Publicar horarios». */
export function publishConfirmMessage(input: PublishConfirmInput): string {
  const day = dayMonthEs(input.date) || input.date;
  const where = input.divisionName ? ` de ${input.divisionName}` : "";
  const lines = [`¿Publicar ${horariosText(input.count)}${where} del ${day}?`];
  if (!input.noticesOpen) {
    lines.push(
      input.closed
        ? "El torneo está cerrado: se marcarán como publicados sin avisar."
        : "Los avisos del torneo están apagados: se marcarán como publicados sin avisar a nadie.",
    );
  } else if (input.players > 0) {
    lines.push(`Cada jugador recibe un solo aviso con su horario de ese día (${jugadoresText(input.players)}).`);
  } else {
    lines.push("Esta vez nadie recibe aviso: estos partidos aún no tienen jugadores a quién avisar.");
  }
  const extras: string[] = [];
  if ((input.hiddenByFilters ?? 0) > 0) extras.push(`Incluye ${input.hiddenByFilters} que tus filtros ocultan.`);
  for (const venue of input.otherVenues ?? []) {
    if (venue.count > 0) extras.push(`Incluye ${venue.count} en ${venue.name}.`);
  }
  if (extras.length > 0) lines.push(extras.join(" "));
  lines.push(
    input.count === 1
      ? "Después de publicar, mover, quitar o posponer este partido pedirá motivo y avisará a sus jugadores."
      : "Después de publicar, mover, quitar o posponer uno de estos partidos pedirá motivo y avisará a sus jugadores.",
  );
  return lines.join("\n");
}

/** Mensaje al terminar (responde al clic: va como mensaje de la acción). */
export function publishSuccessMessage(input: {
  published: number;
  notified: number;
  date: string;
  divisionName?: string | null;
  /** true = los avisos del torneo estaban apagados. */
  silent?: boolean;
}): string {
  if (input.published === 0) {
    return "No había horarios sin publicar ese día (alguien más ya los publicó).";
  }
  const day = shortDayMonthEs(input.date) || input.date;
  const scope = input.divisionName ? `${input.published} de ${input.divisionName} · ${day}` : `${input.published} · ${day}`;
  if (input.silent) {
    return `Horarios publicados: ${scope}. No se avisó a nadie: los avisos del torneo están apagados.`;
  }
  if (input.notified === 0) {
    return `Horarios publicados: ${scope}. Ningún jugador recibió aviso.`;
  }
  return `Horarios publicados: ${scope}. Avisamos a ${jugadoresText(input.notified)}.`;
}

/** ¿Tiene al menos un jugador? (la Final vs «por definir» sí: se avisa al
 * lado conocido; sin ninguno no hay a quién avisar). */
export function hasKnownPlayer(match: {
  player1_participant_id?: number | null;
  player2_participant_id?: number | null;
}): boolean {
  return match.player1_participant_id != null || match.player2_participant_id != null;
}

/** Qué pasa al Confirmar en la barra del grid. */
export function scheduleBarConsequence(published: boolean, reason?: string | null, hasPlayers = true): string {
  if (!published) return "Sin publicar: nadie recibe aviso.";
  if (!hasPlayers) return "Ya publicado, pero aún no tiene jugadores: nadie recibe aviso.";
  const cleaned = (reason ?? "").trim();
  return `Ya publicado: se avisará a sus jugadores${cleaned ? ` · Motivo: ${cleaned}` : ""}.`;
}

/** Después de mover un partido ya publicado: lo que de verdad pasó con el
 * aviso (el servidor responde `players_notified`). Nunca dice «Avisamos» si
 * no salió nada (sin jugadores, avisos del torneo apagados o el jugador aún
 * no ve el calendario), para que el organizador avise por su cuenta. */
export function publishedMoveNotice(input: { notified: boolean; hasPlayers: boolean; reason?: string | null }): string {
  const cleaned = (input.reason ?? "").trim();
  if (input.notified) return `Avisamos a sus jugadores${cleaned ? ` (motivo: ${cleaned})` : ""}.`;
  if (!input.hasPlayers) return "Aún no tiene jugadores: nadie recibió aviso.";
  return "No salió aviso: los avisos del torneo están apagados o los jugadores aún no ven el calendario.";
}
